import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { chatApi, streamMessage } from '@/api/chat'
import { useLicense } from '@/features/billing/useLicense'
import { useChatStore } from '@/store/chat'
import { isLicenseError } from '@/store/license'
import type { ChatMessage, ChatThread, ChatTool, PendingAction } from '@/types/api'

/**
 * One conversation: its messages (cached under ['chat-thread', id]), the
 * reply being streamed, and the actions the user can confirm. Anything the
 * assistant changes invalidates the pages' queries so they update behind
 * the panel.
 */
export interface Draft {
  content: string
  tools: ChatTool[]
  actions: PendingAction[]
}

export interface ChatFailure {
  message: string
  retry?: () => void
}

/** Tools whose completion may have changed data a page shows. */
const WRITE_TOOL = /apply|pass|retry|cancel|submit|setting|sync|find|rescore/i

const DATA_KEYS = [
  ['applications'],
  ['application-stats'],
  ['dashboard-summary'],
  ['top-matches'],
  ['recommendations'],
  ['application-settings'],
  ['job-sources'],
  // The plan chip's "N left" after the assistant applies.
  ['billing', 'subscription'],
]

export function invalidateAppData(queryClient: QueryClient): void {
  for (const queryKey of DATA_KEYS) void queryClient.invalidateQueries({ queryKey })
}

const COULD_NOT_REACH = 'Could not reach the assistant'

function upsert<T extends { id: string }>(list: T[], item: T): T[] {
  const index = list.findIndex((entry) => entry.id === item.id)
  if (index === -1) return [...list, item]
  const next = list.slice()
  next[index] = { ...next[index], ...item }
  return next
}

/** Confirming one of these sends applications: it needs a plan with applications left. */
const APPLY_KINDS = new Set<PendingAction['kind']>(['apply', 'retry', 'submit_ready', 'retry_captcha'])

export function useChat() {
  const { requireApply } = useLicense()
  const queryClient = useQueryClient()
  const threadId = useChatStore((state) => state.threadId)
  const setThreadId = useChatStore((state) => state.setThreadId)

  const thread = useQuery({
    queryKey: ['chat-thread', threadId],
    queryFn: () => chatApi.thread(threadId as string),
    enabled: Boolean(threadId),
    retry: false,
    staleTime: Infinity,
  })

  // A thread removed elsewhere (or a stale id in storage) falls back to a fresh chat.
  useEffect(() => {
    if (thread.error instanceof ApiError && thread.error.status === 404) setThreadId(null)
  }, [thread.error, setThreadId])

  const [draft, setDraftState] = useState<Draft | null>(null)
  const draftRef = useRef<Draft | null>(null)
  const setDraft = useCallback((update: Draft | null | ((current: Draft | null) => Draft | null)) => {
    const next = typeof update === 'function' ? update(draftRef.current) : update
    draftRef.current = next
    setDraftState(next)
  }, [])

  const [streaming, setStreaming] = useState(false)
  const [streamError, setStreamError] = useState<string | null>(null)
  const [failure, setFailure] = useState<ChatFailure | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  const abort = useRef<AbortController | null>(null)

  useEffect(() => () => abort.current?.abort(), [])

  const appendMessage = useCallback(
    (id: string, message: ChatMessage) => {
      queryClient.setQueryData<ChatThread>(['chat-thread', id], (old) =>
        old
          ? { ...old, messages: [...old.messages.filter((entry) => entry.id !== message.id), message], updatedAt: message.createdAt }
          : { id, title: 'New chat', updatedAt: message.createdAt, messages: [message] },
      )
    },
    [queryClient],
  )

  const patchAction = useCallback(
    (id: string, action: PendingAction) => {
      queryClient.setQueryData<ChatThread>(['chat-thread', id], (old) =>
        old
          ? {
              ...old,
              messages: old.messages.map((message) =>
                message.actions?.some((entry) => entry.id === action.id)
                  ? { ...message, actions: message.actions.map((entry) => (entry.id === action.id ? { ...entry, ...action } : entry)) }
                  : message,
              ),
            }
          : old,
      )
      setDraft((current) => (current ? { ...current, actions: current.actions.map((entry) => (entry.id === action.id ? { ...entry, ...action } : entry)) } : current))
    },
    [queryClient, setDraft],
  )

  /** Whatever streamed so far becomes a message, so a stopped reply is not lost. */
  const settleDraft = useCallback(
    (id: string) => {
      const current = draftRef.current
      if (current && (current.content.trim() || current.tools.length || current.actions.length)) {
        appendMessage(id, {
          id: `local-assistant-${Date.now()}`,
          role: 'assistant',
          content: current.content,
          createdAt: new Date().toISOString(),
          tools: current.tools.filter((tool): tool is ChatTool & { status: 'done' | 'failed' } => tool.status !== 'started'),
          actions: current.actions,
        })
      }
      setDraft(null)
    },
    [appendMessage, setDraft],
  )

  const send = useCallback(
    async (text: string) => {
      const content = text.trim()
      if (!content || streaming || confirming) return
      setStreamError(null)
      setFailure(null)

      let id = threadId
      if (!id) {
        try {
          const created = await chatApi.createThread()
          id = created.id
          queryClient.setQueryData<ChatThread>(['chat-thread', id], { ...created, messages: created.messages ?? [] })
          setThreadId(id)
          void queryClient.invalidateQueries({ queryKey: ['chat-threads'] })
        } catch {
          setFailure({ message: COULD_NOT_REACH, retry: () => void send(content) })
          return
        }
      }
      const owner = id

      appendMessage(owner, { id: `local-user-${Date.now()}`, role: 'user', content, createdAt: new Date().toISOString() })
      setDraft({ content: '', tools: [], actions: [] })
      setStreaming(true)
      const controller = new AbortController()
      abort.current = controller
      let final = false

      try {
        await streamMessage(
          owner,
          content,
          {
            onToken: (token) => setDraft((current) => (current ? { ...current, content: current.content + token } : current)),
            onTool: (tool) => {
              setDraft((current) => (current ? { ...current, tools: upsert(current.tools, tool) } : current))
              if (tool.status === 'done' && WRITE_TOOL.test(tool.name)) invalidateAppData(queryClient)
            },
            onAction: (action) => setDraft((current) => (current ? { ...current, actions: upsert(current.actions, action) } : current)),
            onMessage: (message) => {
              final = true
              const current = draftRef.current
              appendMessage(owner, {
                ...message,
                tools: message.tools ?? current?.tools.filter((tool): tool is ChatTool & { status: 'done' | 'failed' } => tool.status !== 'started'),
                actions: message.actions ?? current?.actions,
              })
              setDraft(null)
            },
            onError: (message) => setStreamError(message),
          },
          controller.signal,
        )
        if (!final) settleDraft(owner)
      } catch (error) {
        if (controller.signal.aborted) {
          settleDraft(owner)
        } else {
          settleDraft(owner)
          const message = error instanceof ApiError && error.status === 401 ? 'Your session has expired. Sign in again.' : COULD_NOT_REACH
          setFailure({ message, retry: () => void send(content) })
        }
      } finally {
        if (abort.current === controller) abort.current = null
        setStreaming(false)
        void queryClient.invalidateQueries({ queryKey: ['chat-threads'] })
      }
    },
    [threadId, streaming, confirming, queryClient, setThreadId, appendMessage, setDraft, settleDraft],
  )

  const stop = useCallback(() => abort.current?.abort(), [])

  const confirm = useCallback(
    async (action: PendingAction) => {
      if (!threadId || confirming) return
      // No plan (or none left this month): the upgrade dialog, and the card stays as it is.
      if (APPLY_KINDS.has(action.kind) && !requireApply(() => undefined)) return
      setConfirming(action.id)
      setFailure(null)
      try {
        const result = await chatApi.confirmAction(threadId, action.id)
        patchAction(threadId, result.action)
        if (result.message) appendMessage(threadId, result.message)
        invalidateAppData(queryClient)
      } catch (error) {
        // A 402 already opened the upgrade dialog; nothing failed to reach anyone.
        if (!isLicenseError(error)) setFailure({ message: COULD_NOT_REACH, retry: () => void confirm(action) })
      } finally {
        setConfirming(null)
      }
    },
    [threadId, confirming, patchAction, appendMessage, queryClient, requireApply],
  )

  const dismiss = useCallback(
    async (action: PendingAction) => {
      if (!threadId || confirming) return
      setConfirming(action.id)
      setFailure(null)
      try {
        const result = await chatApi.dismissAction(threadId, action.id)
        patchAction(threadId, result.action)
      } catch {
        setFailure({ message: COULD_NOT_REACH, retry: () => void dismiss(action) })
      } finally {
        setConfirming(null)
      }
    },
    [threadId, confirming, patchAction],
  )

  /** Switch threads (null starts a fresh chat); a reply in flight is stopped. */
  const switchThread = useCallback(
    (id: string | null) => {
      abort.current?.abort()
      setDraft(null)
      setStreamError(null)
      setFailure(null)
      setThreadId(id)
    },
    [setDraft, setThreadId],
  )

  return {
    threadId,
    thread: thread.data ?? null,
    loading: Boolean(threadId) && thread.isLoading,
    messages: thread.data?.messages ?? [],
    draft,
    streaming,
    streamError,
    failure,
    confirming,
    send,
    stop,
    confirm,
    dismiss,
    switchThread,
  }
}
